import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CommonProfileService,
  ExpenseProfileService,
  TaxProcedureProfileService,
} from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetExpenseTypeService } from "subModule/src/services/expense/useExpense";
import dayjs from "dayjs";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { AddExpenseResponse } from "subModule/src/services/expense/interface";
import Loader from "layout/Loader";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import CancelIcon from "@mui/icons-material/Cancel";
import { Wrapper } from "@subModule/src/type";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { useGetPaymentModesDropdownService } from "subModule/src/services/payment/usePayments";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import { GetDefaultTaxProcedureResponse } from "subModule/src/services/taxProcedure/interface";
import { selectUserInfo } from "features/user/userSlice";
import Currency from "common/Others/Currency";
import {
  extractMediaFileName,
  fetchMediaAsDataUrlMap,
  isImageFileName,
} from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function ExpensesModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    watch,
    reset,
    trigger,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      date: dayjs(new Date())?.format("YYYY-MM-DD"),
      refDate: "",
    },
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "expenses",
    moduleHref: "/expenses",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  const [fileName, setFileName] = useState<string>("");
  const [deleting, setDeleting] = useState<boolean>(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState<
    Record<string, string>
  >({});
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  /** When payment-mode filtered accounts are empty, reload without paymentMode. */
  const [useAllAccountsFallback, setUseAllAccountsFallback] = useState(false);
  const useAllAccountsFallbackRef = useRef(false);
  const pendingAccountsFallbackRefetch = useRef(false);
  const { data: typesData, refetch: typesRefetch } = useGetExpenseTypeService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: paymentModesData, refetch: paymentModesRefetch } =
    useGetPaymentModesDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const selectedPaymentModeId = watch("paymentMode")?.value;
  const canLoadAccounts = Boolean(selectedPaymentModeId);
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: pageNo,
      size: 50,
      search: dropdownSearchTerm,
      paymentMode: useAllAccountsFallback ? undefined : selectedPaymentModeId,
      active: true,
    });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const loggedInUserData = useSelector(selectUserInfo);
  const loggedInUserCurrency = loggedInUserData?.business?.currency;
  const currencyOptions = Currency.map((item) => ({
    value: item.value,
    label: item.title,
    symbol: item.symbol,
  }));
  const currencyLabel =
    currencyOptions.find((option: any) => option.value === loggedInUserCurrency)
      ?.symbol || "";
  const types = (typesData?.pages[0]?.data?.content || []).map(
    (item?: any) => ({ value: item?.id, label: item?.title }),
  );
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const paymentModes = (paymentModesData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.name }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const taxProcedures = (taxProceduresData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string; percentageValue?: number }) => ({
      value: item.id,
      label: item.title,
      percentageValue: item.percentageValue,
    }),
  );
  useEffect(() => {
    typesRefetch();
    stationsRefetch();
    vendorsRefetch();
    paymentModesRefetch();
    taxProceduresRefetch();
  }, []);
  useEffect(() => {
    if (params?.id && stationsData && typesData && taxProceduresData) {
      if (loading) getByID();
    }
  }, [params, stationsData, typesData, taxProceduresData]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  useEffect(() => {
    useAllAccountsFallbackRef.current = useAllAccountsFallback;
  }, [useAllAccountsFallback]);
  useEffect(() => {
    setUseAllAccountsFallback(false);
    useAllAccountsFallbackRef.current = false;
  }, [selectedPaymentModeId]);
  useEffect(() => {
    if (
      !useAllAccountsFallback ||
      !pendingAccountsFallbackRefetch.current ||
      !canLoadAccounts
    )
      return;
    pendingAccountsFallbackRefetch.current = false;
    setSearchLoading("accounts");
    bankAccountsRefetch().finally(() => setSearchLoading(null));
  }, [useAllAccountsFallback, canLoadAccounts, bankAccountsRefetch]);
  const getAccountsContent = (res: any) =>
    res?.data?.pages?.[0]?.data?.content ||
    res?.pages?.[0]?.data?.content ||
    [];
  const maybeFallbackToAllAccounts = (res: any, action: string) => {
    if (
      action === "loadNext" ||
      useAllAccountsFallbackRef.current ||
      !selectedPaymentModeId
    )
      return false;
    const content = getAccountsContent(res);
    if (content.length > 0) return false;
    pendingAccountsFallbackRefetch.current = true;
    setUseAllAccountsFallback(true);
    return true;
  };
  const getDefaultTaxProcedure = () => {
    TaxProcedureProfileService.getDefaultTaxProcedure(5)
      .then((res: GetDefaultTaxProcedureResponse) => {
        if (res.success) {
          setValue("taxProcedureId", {
            value: res.data?.id,
            label: res.data?.title,
            percentageValue: res.data?.percentageValue,
          });
          setValue(
            "taxValue",
            (
              watch("amount") *
              ((res?.data?.percentageValue || 0) / 100)
            )?.toFixed(2),
          );
          setValue(
            "amountWithTax",
            parseFloat(watch("amount")) +
              watch("amount") * ((res?.data?.percentageValue || 0) / 100),
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const getByID = () => {
    ExpenseProfileService.getExpensesById(+(params as any)?.id)
      .then(async (res: AddExpenseResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            type: res?.data?.type
              ? {
                  value: res?.data?.type,
                  label: res?.data?.typeTitle || res?.data?.type,
                }
              : null,
            stationId: res?.data?.stationId
              ? {
                  value: res?.data?.stationId,
                  label: res?.data?.stationTitle || res?.data?.stationId,
                }
              : null,
            supplierId: res?.data?.supplierId
              ? {
                  value: res?.data?.supplierId,
                  label: res?.data?.supplierTitle || res?.data?.supplierId,
                }
              : null,
            paymentMode: res.data?.paymentMode
              ? {
                  value: res.data?.paymentMode,
                  label: res?.data?.paymentModeTitle || res.data?.paymentMode,
                }
              : null,
            bankAccountId: res.data?.bankAccountId
              ? {
                  value: res.data?.bankAccountId,
                  label:
                    res?.data?.bankAccountTitle ||
                    res?.data?.accountTitle ||
                    res.data?.bankAccountId,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res?.data?.taxProcedureTitle || res.data?.taxProcedureId,
                  percentageValue: res?.data?.taxPercentageValue || 0,
                }
              : null,
            taxValue: (
              (res?.data?.amount || 0) *
              ((res?.data?.taxPercentageValue || 0) / 100)
            )?.toFixed(2),
          });
          setValue(
            "amountWithTax",
            parseFloat(watch("amount")) + parseFloat(watch("taxValue")),
          );
          const urls = Array.isArray(res?.data?.url) ? res.data.url : [];
          const previews = await fetchMediaAsDataUrlMap(urls);
          setAttachmentPreviews(previews);
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setDeleting(false));
  };
  const DeleteAttachment = (fileName: string) => {
    CommonProfileService.deleteMedia("media/delete", fileName, 6)
      .then((response: Wrapper) => {
        if (response.success) {
          getByID();
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("deleted") + " " + t("successfully")}
            />,
            { variant: "success" },
          );
        } else {
          setDeleting(false);
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        setDeleting(false);
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const files: File[] = Array.isArray(data.file)
      ? data.file
      : data.file
        ? [data.file]
        : [];
    for (const f of files) {
      if (
        ![
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/webp",
        ].includes(f.type) &&
        !/\.(xls|xlsx|csv|pdf|jpg|jpeg|png|webp)$/i.test(f.name)
      ) {
        enqueueSnackbar(<AlertMessages text={t("imagesExcelPdfAllowed")} />, {
          variant: "error",
        });
        setIsBtnEnabled(true);
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        enqueueSnackbar(<AlertMessages text={t("fileSmaller5MB")} />, {
          variant: "error",
        });
        setIsBtnEnabled(true);
        return;
      }
    }
    if (params?.id) {
      delete data?.typeTitle;
      delete data?.createdAt;
    }
    delete data.taxValue;
    delete data.amountWithTax;
    delete data.tax;
    const payload: any = {
      ...data,
      type: data.type.value,
      stationId: data.stationId.value,
      date: data.date.split("T")[0],
      supplierId: data.supplierId.value,
      refDate: data?.refDate
        ? data?.refDate?.split?.("T")?.[0] || data.refDate
        : "",
      bankAccountId: data.bankAccountId.value,
      paymentMode: data?.paymentMode.value,
      taxProcedureId:
        data?.isTaxable && data?.taxProcedureId?.value
          ? data?.taxProcedureId?.value
          : 0,
    };
    if (!files.length) delete payload?.file;
    if (params?.id) {
      ExpenseProfileService.updateExpense(payload)
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("expense") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ExpenseProfileService.addExpense(payload)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("expense") + " " + t("added") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
            navigate(`/expense/view/${response?.data?.id}`);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext" | "open",
    search?: string,
  ) => {
    if (action === "open") {
      setPageNo(1);
      setDropdownSearchTerm("");
      setSearchLoading(typeName);
    } else if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => {
        if (typeName === "types")
          typesRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "stations")
          stationsRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "suppliers")
          vendorsRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "paymentModes")
          paymentModesRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "accounts") {
          if (!canLoadAccounts) {
            setSearchLoading(null);
            return;
          }
          bankAccountsRefetch()
            .then((res) => {
              if (maybeFallbackToAllAccounts(res, action)) return;
              setSearchLoading(null);
            })
            .catch(() => setSearchLoading(null));
        } else if (typeName === "taxProcedures")
          taxProceduresRefetch().finally(() => setSearchLoading(null));
      },
      action === "open" ? 0 : 300,
    );
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editExpense") : t("addExpense")}
        icon={
          <PriceChangeOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_EXPENSE_MANAGER_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? t("editExpense") : t("addExpense")}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              {deleting ? (
                <Box sx={{ "& .MuiBackdrop-root": { zIndex: 999 } }}>
                  <Loader />
                </Box>
              ) : null}
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("title", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={t("title") + " *"}
                  placeholder={t("phExpenseTitle")}
                  title={t("title")}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="type"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("type") + " *"}
                      defaultPlaceholder="phSelectExpenseType"
                      {...field}
                      options={types}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                      }}
                      error={errors.type?.message as string}
                      isLoading={searchLoading === "types"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("types", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions("types", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(typesData?.pages[0]?.data as any).last)
                          handleDropdownActions("types", "loadNext");
                      }}
                      page={pageNo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="supplierId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("vendors") + " *"}
                      defaultPlaceholder="phSelectSupplier"
                      {...field}
                      options={supplierOptions}
                      isSearchable
                      onChange={(selectedOption: any) =>
                        field.onChange(selectedOption)
                      }
                      error={errors.supplierId?.message as string}
                      isLoading={searchLoading === "suppliers"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "suppliers",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions("suppliers", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(vendorsData?.pages[0]?.data as any).last)
                          handleDropdownActions("suppliers", "loadNext");
                      }}
                      page={pageNo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("referenceNumber", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={t("referenceNo") + " *"}
                  placeholder={t("phExpenseReferenceNo")}
                  title={t("referenceNo")}
                  fullWidth
                  error={!!errors.referenceNumber}
                  helperText={errors.referenceNumber?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="date"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? t("Invalid Date") : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("date") + " *"}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.date?.message as string}
                      maxDate={dayjs()}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="refDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) && value
                        ? t("Invalid Date")
                        : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("referenceDate")}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.refDate?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="isTaxable"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <CommonCheckbox
                      {...field}
                      label={t("taxApplied")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        field.onChange(event.target.checked);
                        if (!event.target.checked) {
                          setValue("taxProcedureId", null);
                        }
                        if (!params?.id && event.target.checked)
                          getDefaultTaxProcedure();
                      }}
                      checked={field.value}
                      color={themeColors?.primary}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                {watch("isTaxable") ? (
                  <Controller
                    name="taxProcedureId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("taxProcedure") + " *"}
                        {...field}
                        options={taxProcedures}
                        isSearchable
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          setValue(
                            "taxValue",
                            (
                              watch("amount") *
                              (watch("taxProcedureId")?.percentageValue / 100)
                            )?.toFixed(2),
                          );
                          setValue(
                            "amountWithTax",
                            parseFloat(watch("amount")) +
                              watch("amount") *
                                (selectedOption.percentageValue / 100),
                          );
                        }}
                        error={errors.taxProcedureId?.message as string}
                        isLoading={searchLoading === "taxProcedures"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "taxProcedures",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions(
                                "taxProcedures",
                                "searchIt",
                                "",
                              );
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(taxProceduresData?.pages[0]?.data as any).last)
                            handleDropdownActions("taxProcedures", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="paymentMode"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("PaymentMode") + " *"}
                      defaultPlaceholder="phSelectPaymentMode"
                      {...field}
                      options={paymentModes}
                      isSearchable
                      onMenuOpen={() =>
                        handleDropdownActions("paymentModes", "open")
                      }
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("bankAccountId", null);
                        setUseAllAccountsFallback(false);
                        useAllAccountsFallbackRef.current = false;
                      }}
                      error={errors.paymentMode?.message as string}
                      isLoading={searchLoading === "paymentModes"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "paymentModes",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions(
                              "paymentModes",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(paymentModesData?.pages[0]?.data as any).last)
                          handleDropdownActions("paymentModes", "loadNext");
                      }}
                      page={pageNo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  {...register("amount", {
                    required: t("This field is required."),
                  })}
                  onChange={(e: any) => {
                    const value = parseFloat(e.target.value);
                    e.target.value = value < 0 ? 0 : value || 0;
                    setValue(
                      "amountWithTax",
                      (
                        parseFloat(e.target.value) +
                        (parseFloat(e.target.value) *
                          parseFloat(
                            watch("taxProcedureId")?.percentageValue || 0,
                          )) /
                          100
                      )?.toFixed(2),
                    );
                    setValue(
                      "taxValue",
                      (
                        parseFloat(e.target.value) *
                        (watch("taxProcedureId")?.percentageValue / 100)
                      )?.toFixed(2),
                    );
                    if (!IsBtnEnabled) setIsBtnEnabled(true);
                  }}
                  label={
                    t("amount") +
                    (loggedInUserCurrency ? " (" + currencyLabel + ")" : "") +
                    " *"
                  }
                  placeholder={t("phExpenseAmount")}
                  title={
                    t("amount") +
                    (loggedInUserCurrency ? " (" + currencyLabel + ")" : "")
                  }
                  fullWidth
                  error={!!errors.amount}
                  helperText={errors.amount?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                {watch("isTaxable") && watch("taxProcedureId")?.value ? (
                  <TextField
                    type="number"
                    {...register("tax")}
                    label={t("tax") + " (%)"}
                    placeholder={t("tax") + " (%)"}
                    title={t("tax") + " (%)"}
                    fullWidth
                    error={!!errors.tax}
                    helperText={errors.tax?.message as string}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <span className="font-Saira text-[13px]">
                            {(watch("taxValue") || 0) +
                              (loggedInUserCurrency ? " " + currencyLabel : "")}
                          </span>
                        </InputAdornment>
                      ),
                    }}
                    value={watch("taxProcedureId")?.percentageValue || 0}
                  />
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                {watch("isTaxable") && watch("taxProcedureId")?.value ? (
                  <TextField
                    type="number"
                    {...register("amountWithTax")}
                    label={
                      t("amountWithTax") +
                      (loggedInUserCurrency ? " (" + currencyLabel + ")" : "")
                    }
                    placeholder={t("phAmountWithTaxAuto")}
                    title={
                      t("amountWithTax") +
                      (loggedInUserCurrency ? " (" + currencyLabel + ")" : "")
                    }
                    fullWidth
                    error={!!errors.amountWithTax}
                    helperText={errors.amountWithTax?.message as string}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      const value = parseFloat(e.target.value);
                      e.target.value = value < 0 ? 0 : value || 0;
                      const amount =
                        parseFloat(e.target.value) /
                        (1 +
                          (watch("taxProcedureId")?.percentageValue || 0) /
                            100);
                      setValue(
                        "amount",
                        amount - Math.floor(amount) > 0.9
                          ? Math.round(amount)
                          : amount?.toFixed(2),
                      );
                      setValue(
                        "taxValue",
                        (
                          watch("amount") *
                          (watch("taxProcedureId")?.percentageValue / 100)
                        )?.toFixed(2),
                      );
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                  />
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="stationId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("station") + " *"}
                      defaultPlaceholder="phSelectStation"
                      {...field}
                      options={stations}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        trigger("stationId");
                      }}
                      error={errors.stationId?.message as string}
                      isLoading={searchLoading === "stations"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("stations", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions("stations", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(stationsData?.pages[0]?.data as any).last)
                          handleDropdownActions("stations", "loadNext");
                      }}
                      page={pageNo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="bankAccountId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <Box>
                      <DynamicSelect
                        label={t("ACCOUNT") + " *"}
                        defaultPlaceholder="phSelectAccount"
                        {...field}
                        options={bankAccounts}
                        isClearable
                        isSearchable
                        isDisabled={!canLoadAccounts}
                        onMenuOpen={() => {
                          if (canLoadAccounts)
                            handleDropdownActions("accounts", "open");
                        }}
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                        }}
                        error={
                          canLoadAccounts
                            ? (errors.bankAccountId?.message as string)
                            : undefined
                        }
                        isLoading={searchLoading === "accounts"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (!canLoadAccounts) return;
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "accounts",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("accounts", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (
                            canLoadAccounts &&
                            !(bankAccountsData?.pages[0]?.data as any).last
                          )
                            handleDropdownActions("accounts", "loadNext");
                        }}
                        page={pageNo}
                      />
                      {!canLoadAccounts ? (
                        <Typography
                          className="font-Saira italic font-[500] text-[11px] mt-1 text-start"
                          sx={{ color: "text.secondary" }}
                        >
                          {t("selectPaymentModeToEnableAccount")}
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...register("description")}
                  label={t("description")}
                  placeholder={t("phExpenseDescription")}
                  title={t("description")}
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message as string}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  rows={3}
                  sx={{
                    "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                      width: "100%",
                      paddingRight: "0px",
                      paddingBottom: 0,
                    },
                    "& .MuiInputBase-inputMultiline": {
                      resize: "block",
                      minHeight: "29px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} id="importfile">
                <Controller
                  name="file"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <>
                      <p className="font-Saira text-[13px] leading-[20px] text-[#343434] font-semibold text-start">
                        {t("files") + " (" + t("max5FilesUpload") + ")"}
                      </p>
                      <input
                        type="file"
                        required
                        accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,.pdf,image/*"
                        style={{ color: fileName ? "inherit" : "transparent" }}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const files = Array.from(event.target.files || []);
                          const existingCount = Array.isArray(watch("url"))
                            ? watch("url").length
                            : 0;
                          if (existingCount + files.length > 5) {
                            field.onChange([]);
                            enqueueSnackbar(
                              <AlertMessages text={t("max5FilesUpload")} />,
                              { variant: "error" },
                            );
                            return;
                          }
                          setFileName(files.map((f) => f.name).join(", "));
                          field.onChange(files);
                        }}
                        title={fileName}
                        multiple
                      />
                    </>
                  )}
                />
                <Box className="flex gap-3 flex-wrap mt-[15px]">
                  {Array.isArray(watch("url")) &&
                    (watch("url") || [])?.map((u: string, i: number) => (
                      <Box className="relative" key={i}>
                        {isImageFileName(u) && attachmentPreviews[u] ? (
                          <MediaImage src={attachmentPreviews[u]} />
                        ) : (
                          <Box
                            onClick={() => {
                              window.open(u, "_blank");
                            }}
                            className="cursor-pointer inline-flex gap-1 items-center rounded-[25px] bg-white py-[7px] px-[12px]"
                            sx={{ "& svg": { width: "25px" } }}
                          >
                            <span className="font-Saira text-[11px] font-semibold text-left">
                              {extractMediaFileName(u) ||
                                u?.split("/")?.pop() ||
                                ""}
                            </span>
                          </Box>
                        )}
                        <CancelIcon
                          sx={{
                            fontSize: 19,
                            fill: themeColors?.primary,
                            position: "absolute",
                            right: "-8px",
                            top: "-10px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                            setDeleting(true);
                            DeleteAttachment(
                              extractMediaFileName(u) ||
                                u?.split("/")?.pop() ||
                                "",
                            );
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  );
}
